// ─── Profil : calendriers de l'espace + superposition perso ──────
// Injectée dans EcranProfil (prop `sections`). DEUX sections distinctes :
//   • Calendriers de l'ESPACE (suite) : créés dans l'espace, partagés avec ses
//     membres — créer/renommer/supprimer + choisir l'actif.
//   • Mes calendriers MoPCal (PERSO) : calendriers où JE suis participant, que je
//     choisis d'afficher dans MA vue (n'expose rien aux autres membres).
// Édition via modale RACINE (ouvrirEditionCalendrier du contexte).
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, Switch, StyleSheet } from 'react-native';
import { useUI, useT, LigneSwipe, confirmerSuppression, useResetOnglet, HIT } from '@mop/core';
import { useCalendrierActif } from './CalendrierActifContext';
import { TYPES_CALENDRIER } from './services/calendriersEspace';

export default function SectionCalendriers() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { calendriersTous: calendriers, calIdActif, ouvrirFicheCalendrier, supprimerCalendrier, setCalIdActif, couleurDe, heritables, estPerso, togglePerso, ouvrirEditionCalendrier, fermerEditionCalendrier, inventorierCalendrier, peutCreer, plusDeJetons, quotaElements, erreurCal, effacerErreurCal } = useCalendrierActif();
  // Clic sur l'onglet Profil → ferme aussi la modale d'édition de calendrier.
  useResetOnglet('Profil', () => fermerEditionCalendrier?.());

  // Supprimer un calendrier detruit TOUT ce qui y est rattache. On l'INVENTORIE
  // d'abord et on l'annonce : une suppression irreversible qui ne dit pas ce
  // qu'elle emporte est un piege — c'est ainsi que des budgets ont disparu.
  const demanderSuppression = async (cal) => {
    const NL = String.fromCharCode(10);
    let detail = '';
    try {
      const { total, parFamille } = await inventorierCalendrier(cal.id);
      if (total > 0) {
        const lignes = Object.entries(parFamille)
          .sort((a, b) => b[1] - a[1])
          .map(([f, n]) => `\u2022 ${n} ${t('suppr.f_' + f)}`).join(NL);
        detail = NL + NL + t('suppr.emporte', { n: total }) + NL + lignes;
      }
    } catch { /* inventaire impossible : on previent quand meme */ }
    confirmerSuppression(
      t('calendrier.supprCalTitre'),
      t('calendrier.supprCalMsg', { nom: cal.nom }) + detail,
      () => supprimerCalendrier(cal.id, cal.nom).catch(() => {}),
    );
  };

  // Ordre alphabetique stable : sans tri, l'ordre suit l'arrivee des documents
  // et change d'un affichage a l'autre.
  const parNom = (l) => [...l].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }));
  const suiteCals = parNom(calendriers.filter(c => c.source === 'suite'));   // calendriers de MES espaces (possédés)
  const accesCals = parNom(calendriers.filter(c => c.source === 'acces'));   // partagés AVEC moi
  const dejaListe = new Set([...suiteCals, ...accesCals].map(c => c.id));
  const persoHeritables = parNom(heritables.filter(h => !dejaListe.has(h.id)));   // MoPCal perso non déjà listés
  const estActif = (id) => calIdActif === id;
  const rien = suiteCals.length === 0 && accesCals.length === 0 && persoHeritables.length === 0 && !peutCreer;

  if (rien) return <Text style={s.vide}>{t('calendrier.aucunCal')}</Text>;

  return (
    <View>
      {/* ── Calendriers de MES espaces (possédés) — créer/éditer/supprimer ── */}
      {(peutCreer || suiteCals.length > 0) && (
        <>
          <View style={s.enteteRow}>
            <Text style={s.groupe}>{t('calendrier.groupeMesEspaces')}</Text>
            {peutCreer && (
              <TouchableOpacity hitSlop={HIT} accessibilityLabel={t('profil.creerCalendrier')} onPress={() => ouvrirEditionCalendrier(null)}>
                <Text style={s.hIc}>＋</Text>
              </TouchableOpacity>
            )}
          </View>
          <Text style={s.aide}>{t('calendrier.aideMesEspaces')}</Text>
          {/* Un echec de creation doit se VOIR : sans ca, on retente en boucle. */}
          {!!erreurCal && (
            <TouchableOpacity onPress={() => effacerErreurCal?.()}>
              <Text style={s.erreur}>⚠️ {erreurCal}</Text>
            </TouchableOpacity>
          )}
          {/* Le compteur ne s'affiche QUE s'il est juste : un chiffre faux est
              pire que pas de chiffre du tout. Tant que le comptage n'est pas
              fiable, on ne dit rien — et on ne pretend surtout pas que le
              maximum est atteint. */}
          {!quotaElements?.sansLimite && quotaElements?.fiable && (
            <Text style={s.aide}>{t('jetons.compteurElements', { n: quotaElements.possedes, max: quotaElements.max })}</Text>
          )}
          {quotaElements?.atteint && <Text style={s.aide}>{t('jetons.creationBloquee', { n: quotaElements.possedes, max: quotaElements.max })}</Text>}
          {suiteCals.length === 0 && <Text style={s.vide}>{t('calendrier.videMesEspaces')}</Text>}
          {suiteCals.map(cal => (
            <LigneSwipe key={cal.id} espacement={7}
              onEditer={() => ouvrirEditionCalendrier({ id: cal.id, nom: cal.nom, emoji: cal.emoji || '📅', type: cal.type || 'general' })}
              onSupprimer={() => demanderSuppression(cal)}>
              <TouchableOpacity style={[s.ligne, { marginBottom: 0 }, estActif(cal.id) && s.ligneOn]} activeOpacity={0.7} onPress={() => ouvrirFicheCalendrier(cal)}>
                <View style={s.ligneTxt}>
                  <View style={[s.pastilleCouleur, { backgroundColor: couleurDe(cal.id) }]} />
                  <Text style={s.emoji}>{cal.emoji}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.nom} numberOfLines={1}>{cal.nom}</Text>
                    <Text style={s.sous}>{TYPES_CALENDRIER.find(tc => tc.id === cal.type) ? t('calendrier.typeCal_' + cal.type) : t('calendrier.titre')}{estActif(cal.id) ? ` · ${t('calendrier.actif')} ★` : ''}</Text>
                  </View>
                </View>
              </TouchableOpacity>
            </LigneSwipe>
          ))}
        </>
      )}

      {/* ── Partagés AVEC moi (accès accordé) — lecture, pas d'édition ── */}
      {accesCals.length > 0 && (
        <>
          <Text style={[s.groupe, { marginTop: suiteCals.length || peutCreer ? 18 : 0 }]}>{t('calendrier.groupePartages')}</Text>
          <Text style={s.aide}>{t('calendrier.aidePartages')}</Text>
          {accesCals.map(cal => (
            <TouchableOpacity key={cal.id} style={[s.ligne, estActif(cal.id) && s.ligneOn]} activeOpacity={0.7} onPress={() => ouvrirFicheCalendrier(cal)}>
              <View style={s.ligneTxt}>
                <View style={[s.pastilleCouleur, { backgroundColor: couleurDe(cal.id) }]} />
                <Text style={s.emoji}>{cal.emoji}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.nom} numberOfLines={1}>{cal.nom}</Text>
                  <Text style={s.sous}>{t('calendrier.partageAvecToi')}{estActif(cal.id) ? ` · ${t('calendrier.actif')} ★` : ''}</Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </>
      )}

      {/* ── Superposition perso : mes calendriers MoPCal ──────────
          On exclut ceux déjà listés au-dessus (possédés / partagés) pour ne pas
          afficher deux fois le même calendrier. */}
      {persoHeritables.length > 0 && (
        <>
          <Text style={[s.groupe, { marginTop: 18 }]}>{t('calendrier.groupePerso')}</Text>
          <Text style={s.aide}>{t('calendrier.aidePerso')}</Text>
          {persoHeritables.map(h => {
            const on = estPerso(h.id);
            return (
              <View key={h.id} style={s.ligne}>
                <View style={s.ligneTxt}>
                  {on ? <View style={[s.pastilleCouleur, { backgroundColor: couleurDe(h.id) }]} /> : <View style={s.pastilleVide} />}
                  <Text style={s.emoji}>{h.emoji || '📆'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.nom} numberOfLines={1}>{h.nom}</Text>
                    <Text style={s.sous}>{on ? t('calendrier.afficheDansVue') : t('calendrier.masque')}</Text>
                  </View>
                </View>
                <Switch value={on} onValueChange={() => togglePerso(h.id)}
                  trackColor={{ false: UI.champBord, true: UI.accent }} thumbColor="#fff" />
              </View>
            );
          })}
        </>
      )}
    </View>
  );
}


const makeStyles = (UI) => StyleSheet.create({
  erreur: { fontSize: 12, color: UI.danger || '#E5397B', lineHeight: 17, marginBottom: 8 },
  vide: { fontSize: 13, color: UI.faint, fontStyle: 'italic' },
  aide: { fontSize: 12, color: UI.muted, lineHeight: 17, marginBottom: 10 },
  enteteRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  hIc: { fontSize: 20, color: UI.accent, fontWeight: '600' },
  groupe: { fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
  ligne: { flexDirection: 'row', alignItems: 'center', borderRadius: 10, borderWidth: 1, borderColor: UI.border, backgroundColor: UI.champ, paddingRight: 8, marginBottom: 7 },
  ligneOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  ligneTxt: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 9, padding: 11 },
  pastilleCouleur: { width: 10, height: 10, borderRadius: 5 },
  pastilleVide: { width: 10, height: 10, borderRadius: 5, borderWidth: 1, borderColor: UI.champBord },
  emoji: { fontSize: 20 },
  nom: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  mini: { padding: 6 },
  miniTxt: { fontSize: 15 },
  ajout: { borderRadius: 10, borderWidth: 1, borderColor: UI.accent, borderStyle: 'dashed', paddingVertical: 11, alignItems: 'center', marginTop: 4 },
  ajoutTxt: { color: UI.accent, fontWeight: '700', fontSize: 13.5 },
  formBloc: { borderRadius: 12, borderWidth: 1, borderColor: UI.border, backgroundColor: UI.card, padding: 12, marginTop: 6 },
  formTitre: { fontSize: 14, fontWeight: '800', color: UI.ink, marginBottom: 4 },
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 10, marginBottom: 7 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  puces: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  puce: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  puceOn: { backgroundColor: UI.accentTint, borderColor: UI.accent },
  puceTxt: { fontSize: 12.5, color: UI.ink, fontWeight: '600' },
  puceTxtOn: { color: UI.accent, fontWeight: '800' },
  formActions: { flexDirection: 'row', gap: 10, marginTop: 14 },
  btnAnnuler: { flex: 1, paddingVertical: 10, borderRadius: 10, alignItems: 'center', borderWidth: 1, borderColor: UI.border },
  btnAnnulerTxt: { color: UI.muted, fontWeight: '700', fontSize: 13.5 },
  btnOk: { flex: 1, paddingVertical: 10, borderRadius: 10, alignItems: 'center', backgroundColor: UI.accent },
  btnOkTxt: { color: '#fff', fontWeight: '800', fontSize: 13.5 },
});
